import { useEffect, useMemo, useState } from "react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Pencil, Power, Trash2, X, Save, Mail, Phone, User, Tag, Briefcase, Store, AlertTriangle } from "lucide-react";
import { SECTORS, NEEDS } from "@/lib/constants";
import { PAYMENT_PREFS, FIXED_EXPENSE_ITEMS } from "@/lib/finance";

const sectorLabel = (v, other) => {
  if (!v) return "—";
  if (v === "diger") return other || "Diğer";
  if (v?.startsWith("custom:")) return v.slice(7);
  return SECTORS.find((s) => s.value === v)?.label || v;
};

function EditModal({ firm, workspace, onClose, onSaved }) {
  const [job, setJob] = useState(null);
  const [f, setF] = useState(() => ({
    brand_name: firm.brand_name || "",
    contact_name: firm.contact_name || "",
    phone: firm.phone || "",
    email: firm.email || "",
    sector: firm.sector || "",
    sector_other: firm.sector_other || "",
    amount: "",
    payment_day: "",
    payment_pref: "",
    needs: [],
    fixed_expenses: { aylik_cekim: null, aylik_reklamci: null, aylik_yazilim: null, aylik_diger: null },
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data: jobs } = await api.get("/finance/recurring-jobs", { params: { workspace } });
      const j = jobs.find((x) => x.brand_name === firm.brand_name);
      if (j) {
        setJob(j);
        const fx = { aylik_cekim: null, aylik_reklamci: null, aylik_yazilim: null, aylik_diger: null };
        (j.fixed_expenses || []).forEach((fe) => { if (fe?.key && fe?.amount != null) fx[fe.key] = String(fe.amount); });
        setF((p) => ({
          ...p,
          amount: j.amount != null ? String(j.amount) : "",
          payment_day: j.payment_day != null ? String(j.payment_day) : "",
          payment_pref: j.payment_pref || "",
          needs: j.needs || [],
          fixed_expenses: fx,
        }));
      }
    } catch {}
  })(); }, [firm.brand_name, workspace]);

  const toggleNeed = (v) => setF((p) => ({ ...p, needs: p.needs.includes(v) ? p.needs.filter((x) => x !== v) : [...p.needs, v] }));
  const toggleFixed = (key) => setF((p) => ({ ...p, fixed_expenses: { ...p.fixed_expenses, [key]: p.fixed_expenses[key] == null ? "" : null } }));
  const setFixedAmt = (key, v) => setF((p) => ({ ...p, fixed_expenses: { ...p.fixed_expenses, [key]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.brand_name.trim()) return setError("Marka adı zorunlu");
    setSaving(true);
    try {
      const fx = FIXED_EXPENSE_ITEMS.filter((it) => f.fixed_expenses[it.key] != null)
        .map((it) => ({ key: it.key, amount: parseFloat(f.fixed_expenses[it.key] || 0) }));
      const payload = {
        brand_name: f.brand_name.trim(),
        contact_name: f.contact_name,
        phone: f.phone,
        email: f.email,
        sector: f.sector,
        sector_other: f.sector_other,
      };
      if (job) {
        // Only send job-scoped changes if we loaded a recurring job for this firm
        if (f.amount !== "") payload.amount = parseFloat(f.amount);
        if (f.payment_day !== "") payload.payment_day = parseInt(f.payment_day);
        payload.payment_pref = f.payment_pref || null;
        payload.needs = f.needs;
        payload.fixed_expenses = fx;
      }
      const { data } = await api.put(`/finance/customers/${firm.id}`, payload);
      toast.success(job
        ? "Firma güncellendi. Fiyat/gider değişiklikleri bir sonraki aydan itibaren geçerli."
        : "Firma güncellendi");
      onSaved(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Güncellenemedi"));
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500";

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center px-6 py-8 overflow-y-auto" data-testid="firm-edit-dialog">
      <div className="bg-white rounded-xl max-w-[720px] w-full p-6 my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-slate-900">Firma Düzenle</h3>
          <button onClick={onClose} data-testid="firm-edit-close"><X size={18} /></button>
        </div>
        {job && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
            Anlaşma bedeli, ödeme günü ve sabit gider değişiklikleri <b>bir sonraki aydan itibaren</b> geçerli olur. Geçmiş aylar korunur.
          </p>
        )}
        <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Tag size={12} /> Marka Adı</label>
            <input value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} className={inp} data-testid="firm-edit-brand" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><User size={12} /> Yetkili Adı</label>
              <input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className={inp} data-testid="firm-edit-contact" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Briefcase size={12} /> Sektör</label>
              <select value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value })} className={inp} data-testid="firm-edit-sector">
                <option value="">—</option>
                {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          {f.sector === "diger" && (
            <input value={f.sector_other} onChange={(e) => setF({ ...f, sector_other: e.target.value })} placeholder="Sektör açıklaması" className={inp} data-testid="firm-edit-sector-other" />
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Phone size={12} /> Telefon</label>
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} data-testid="firm-edit-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Mail size={12} /> E-posta</label>
              <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="firm-edit-email" />
            </div>
          </div>

          {job && (
            <>
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Tekrar Eden İş Bilgileri</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Aylık Anlaşma Bedeli</label>
                    <input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inp} data-testid="firm-edit-amount" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Ödeme Günü</label>
                    <select value={f.payment_day} onChange={(e) => setF({ ...f, payment_day: e.target.value })} className={inp} data-testid="firm-edit-day">
                      <option value="">—</option>{Array.from({length:31},(_,i)=>i+1).map((d)=><option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Ödeme Tercihi</label>
                    <select value={f.payment_pref} onChange={(e) => setF({ ...f, payment_pref: e.target.value })} className={inp} data-testid="firm-edit-pref">
                      <option value="">—</option>{PAYMENT_PREFS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-xs font-semibold text-slate-600 mb-2 block">Çalışma Kapsamı</label>
                  <div className="flex flex-wrap gap-2">
                    {NEEDS.map((n) => { const on = f.needs.includes(n.value); return (
                      <button key={n.value} type="button" onClick={() => toggleNeed(n.value)} data-testid={`firm-edit-need-${n.value}`}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${on ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {n.label}
                      </button>
                    );})}
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-xs font-semibold text-slate-600 mb-2 block">Sabit Aylık Giderler</label>
                  <div className="grid grid-cols-2 gap-3">
                    {FIXED_EXPENSE_ITEMS.map((it) => {
                      const val = f.fixed_expenses[it.key]; const on = val != null;
                      return (
                        <div key={it.key} className={`rounded-lg border ${on ? "border-emerald-300" : "border-slate-200"} bg-white p-2.5`}>
                          <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
                            <input type="checkbox" checked={on} onChange={() => toggleFixed(it.key)} data-testid={`firm-edit-fx-${it.key}`} className="w-4 h-4 accent-emerald-600" />
                            <span className="font-medium">{it.label}</span>
                          </label>
                          <input type="number" step="0.01" disabled={!on} value={val ?? ""} onChange={(e) => setFixedAmt(it.key, e.target.value)}
                            placeholder="0 ₺" data-testid={`firm-edit-fx-amt-${it.key}`} className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-xs disabled:bg-slate-50 disabled:text-slate-400" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="firm-edit-error">{error}</div>}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 mt-4">
            <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2 text-sm">İptal</button>
            <button type="submit" disabled={saving} data-testid="firm-edit-save"
              className="rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save size={13} /> {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel, onConfirm, onClose, tone = "red", testId }) {
  const [busy, setBusy] = useState(false);
  const doIt = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  const btn = tone === "red"
    ? "bg-red-600 hover:bg-red-700"
    : "bg-emerald-800 hover:bg-emerald-700";
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center px-6" data-testid={testId}>
      <div className="bg-white rounded-xl max-w-[440px] w-full p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-600 mb-5 whitespace-pre-wrap">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2 text-sm">İptal</button>
          <button onClick={doIt} disabled={busy} data-testid={`${testId}-confirm`}
            className={`rounded-full text-white px-5 py-2 text-sm font-semibold disabled:opacity-50 ${btn}`}>
            {busy ? "İşleniyor…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Firms() {
  const { workspace } = useWorkspace();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("active"); // active | passive | all
  const [editing, setEditing] = useState(null);
  const [toggleDlg, setToggleDlg] = useState(null); // {firm, targetActive}
  const [deleteDlg, setDeleteDlg] = useState(null); // {firm, hard}

  const load = async () => {
    const { data } = await api.get("/finance/customers", { params: { workspace } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items
      .filter((r) => tab === "all" ? true : tab === "active" ? r.active !== false : r.active === false)
      .filter((r) => !needle || [r.brand_name, r.contact_name, r.phone, r.email].some((v) => (v || "").toLowerCase().includes(needle)));
  }, [items, q, tab]);

  const counts = useMemo(() => ({
    active: items.filter((r) => r.active !== false).length,
    passive: items.filter((r) => r.active === false).length,
    all: items.length,
  }), [items]);

  const doToggle = async () => {
    const { firm, targetActive } = toggleDlg;
    try {
      await api.put(`/finance/customers/${firm.id}`, { active: targetActive });
      toast.success(targetActive ? "Firma yeniden aktif edildi" : "Firma pasife alındı");
      setToggleDlg(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "İşlem başarısız"));
    }
  };

  const doDelete = async () => {
    const { firm, hard } = deleteDlg;
    try {
      await api.delete(`/finance/customers/${firm.id}`, { params: hard ? { hard: true } : {} });
      toast.success(hard
        ? "Firma ve tüm ilişkili kayıtlar tamamen silindi"
        : "Firma pasife alındı (geçmiş kayıtlar korundu)");
      setDeleteDlg(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "İşlem başarısız"));
    }
  };

  const Tab = ({ id, label, count }) => (
    <button onClick={() => setTab(id)} data-testid={`firms-tab-${id}`}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {label} <span className={`ml-1.5 text-xs ${tab === id ? "text-white/70" : "text-slate-400"}`}>{count}</span>
    </button>
  );

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3"><Store size={28} className="text-emerald-700" /> Firmalar</h1>
          <p className="text-sm text-slate-500 mt-2">Firma bilgilerini düzenleyin, aktif/pasif yapın veya silin. Silinen firmalar pasife alınır ve geçmiş kayıtları korunur.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marka, yetkili, telefon…" data-testid="firms-search"
            className="h-11 w-[300px] rounded-lg border border-slate-200 bg-white pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500" />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Tab id="active" label="Aktif" count={counts.active} />
        <Tab id="passive" label="Pasif" count={counts.passive} />
        <Tab id="all" label="Tümü" count={counts.all} />
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/40">
              {["Marka", "Yetkili", "Telefon", "E-posta", "Sektör", "Durum", "İşlem"].map((h) => (
                <th key={h} className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-14 text-center text-sm text-slate-500" data-testid="firms-empty">Firma bulunamadı.</td></tr>
            ) : filtered.map((r) => {
              const passive = r.active === false;
              return (
                <tr key={r.id} className={`border-b border-slate-50 last:border-none hover:bg-slate-50/40 ${passive ? "opacity-60" : ""}`} data-testid={`firm-row-${r.id}`}>
                  <td className="py-4 px-6 text-sm font-medium text-slate-900">{r.brand_name}</td>
                  <td className="py-4 px-6 text-sm text-slate-700">{r.contact_name || "—"}</td>
                  <td className="py-4 px-6 text-sm text-slate-700 whitespace-nowrap">{r.phone || "—"}</td>
                  <td className="py-4 px-6 text-sm text-slate-700">{r.email || "—"}</td>
                  <td className="py-4 px-6 text-sm text-slate-700">{sectorLabel(r.sector, r.sector_other)}</td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium border ${passive ? "bg-slate-50 text-slate-500 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`firm-status-${r.id}`}>
                      <span className={`w-2 h-2 rounded-full ${passive ? "bg-slate-400" : "bg-emerald-500"}`} />
                      {passive ? "Pasif" : "Aktif"}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setEditing(r)} title="Düzenle" data-testid={`firm-edit-${r.id}`}
                        className="w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600 hover:text-slate-900 inline-flex items-center justify-center">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => setToggleDlg({ firm: r, targetActive: passive })} title={passive ? "Aktife Al" : "Pasife Al"} data-testid={`firm-toggle-${r.id}`}
                        className={`w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-slate-100 ${passive ? "text-emerald-600 hover:text-emerald-700" : "text-amber-600 hover:text-amber-700"}`}>
                        <Power size={14} />
                      </button>
                      <button onClick={() => setDeleteDlg({ firm: r })} title="Sil (Pasife Al)" data-testid={`firm-delete-${r.id}`}
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-red-500 hover:text-red-600 inline-flex items-center justify-center">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <EditModal firm={editing} workspace={workspace} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {toggleDlg && (
        <ConfirmDialog
          testId="firm-toggle-dlg"
          title={toggleDlg.targetActive ? "Firmayı Aktife Al" : "Firmayı Pasife Al"}
          message={toggleDlg.targetActive
            ? `${toggleDlg.firm.brand_name} yeniden aktif olacak ve gelecek aylarda otomatik bilanço satırı oluşturulmaya devam edecek.`
            : `${toggleDlg.firm.brand_name} pasife alınacak. Bu ayın kaydı korunur; gelecek aylar için otomatik bilanço satırı üretilmez.`}
          confirmLabel={toggleDlg.targetActive ? "Aktife Al" : "Pasife Al"}
          tone={toggleDlg.targetActive ? "green" : "red"}
          onConfirm={doToggle}
          onClose={() => setToggleDlg(null)}
        />
      )}
      {deleteDlg && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center px-6" data-testid="firm-delete-dlg">
          <div className="bg-white rounded-xl max-w-[500px] w-full p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle size={18} className="text-red-500" /> {deleteDlg.firm.brand_name}</h3>
              <button onClick={() => setDeleteDlg(null)}><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-600 mb-5">İki seçenek var. Lütfen dikkatli seçin:</p>
            <div className="space-y-3">
              <button onClick={() => { setDeleteDlg({ ...deleteDlg, hard: false }); doDelete(); }}
                data-testid="firm-delete-soft"
                className="w-full text-left rounded-lg border border-amber-200 bg-amber-50/40 hover:bg-amber-50 px-4 py-3">
                <div className="text-sm font-semibold text-amber-900">Pasife Al (Önerilen)</div>
                <div className="text-xs text-amber-700 mt-0.5">Firma listelerden gizlenir ama geçmiş faturalar, ödemeler, gelirler korunur. Gelecek aylar için yeni satır üretilmez.</div>
              </button>
              <button onClick={() => { setDeleteDlg({ ...deleteDlg, hard: true }); doDelete(); }}
                data-testid="firm-delete-hard"
                className="w-full text-left rounded-lg border border-red-200 bg-red-50/40 hover:bg-red-50 px-4 py-3">
                <div className="text-sm font-semibold text-red-800">Tamamen Sil (Geri Alınamaz)</div>
                <div className="text-xs text-red-700 mt-0.5">Firma + tüm tekrar eden işler, tek seferlik işler, aylık bilanço satırları ve giderler silinir. Ciro rakamları buna göre güncellenir.</div>
              </button>
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={() => setDeleteDlg(null)} className="rounded-full border border-slate-200 px-5 py-2 text-sm">İptal</button>
            </div>
          </div>
        </div>
      )}
    </FinanceLayout>
  );
}
